import React, { cloneElement, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import "./tooltip.css"

const viewportPadding = 12

const Tooltip = ({
  children,
  content,
  placement = "bottom",
  offset = 10,
  maxWidth = 260,
}) => {
  const triggerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    isPositioned: false,
  })

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) {
      return
    }

    const triggerBounds = triggerRef.current.getBoundingClientRect()
    const tooltipBounds = tooltipRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const shouldPlaceAbove =
      placement === "top" ||
      (placement === "bottom" &&
        triggerBounds.bottom + offset + tooltipBounds.height >
          viewportHeight - viewportPadding)
    const top = shouldPlaceAbove
      ? triggerBounds.top - offset - tooltipBounds.height
      : triggerBounds.bottom + offset
    const left = Math.min(
      Math.max(
        viewportPadding,
        triggerBounds.left + triggerBounds.width / 2 - tooltipBounds.width / 2
      ),
      viewportWidth - tooltipBounds.width - viewportPadding
    )

    setPosition({
      top: Math.max(viewportPadding, top),
      left,
      isPositioned: true,
    })
  }

  useEffect(() => {
    if (isOpen) {
      updatePosition()
    }
  }, [isOpen, content, placement, offset, maxWidth])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)

    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [isOpen])

  const child = React.Children.only(children)
  const childProps = {
    ref: element => {
      triggerRef.current = element
    },
    onMouseEnter: event => {
      if (child.props.onMouseEnter) {
        child.props.onMouseEnter(event)
      }
      setIsOpen(true)
    },
    onMouseLeave: event => {
      if (child.props.onMouseLeave) {
        child.props.onMouseLeave(event)
      }
      setIsOpen(false)
      setPosition(currentPosition => ({
        ...currentPosition,
        isPositioned: false,
      }))
    },
    onFocus: event => {
      if (child.props.onFocus) {
        child.props.onFocus(event)
      }
      setIsOpen(true)
    },
    onBlur: event => {
      if (child.props.onBlur) {
        child.props.onBlur(event)
      }
      setIsOpen(false)
      setPosition(currentPosition => ({
        ...currentPosition,
        isPositioned: false,
      }))
    },
  }

  return (
    <>
      {cloneElement(child, childProps)}
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            ref={tooltipRef}
            className="tooltip"
            role="tooltip"
            data-positioned={position.isPositioned}
            style={{
              top: position.top,
              left: position.left,
              maxWidth:
                typeof maxWidth === "number"
                  ? `min(${maxWidth}px, calc(100vw - ${viewportPadding * 2}px))`
                  : maxWidth,
            }}
          >
            {content}
          </span>,
          document.body
        )}
    </>
  )
}

export default Tooltip
